import type { ColDef } from 'ag-grid-community'

/**
 * Smart Filter Expression Syntax (clinical table data):
 *
 * Clinical tables often have cells like "3 (75.0%)" where the leading number
 * is a count and the bracket value is a percentage/derived stat. Users need
 * to filter on either part independently:
 *
 *   ">5"    → compare leading number (count)
 *   ">50%"  → compare percentage inside parentheses
 *   "(>10"  → compare bracket value (works with or without %)
 *
 * These patterns power the expression parser in parseFilterExpression().
 */
export const PERCENTAGE_PATTERN = /\(\s*(\d+(?:\.\d+)?)\s*%\s*\)/
export const BRACKET_NUMBER_PATTERN = /\(\s*(-?\d+(?:\.\d+)?)\s*%?\s*\)/
export const FILTER_EXPRESSION_PATTERN = /^\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)\s*(%)?\s*$/
/** Matches bracket-prefixed expressions like "(>10", "(<50", "(>=3.5" */
export const BRACKET_FILTER_PATTERN = /^\s*\(\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)\s*$/

export interface TableGridData {
  columnDefs: ColDef[]
  rowData: Record<string, string>[]
}

/**
 * Parsed result of a filter expression typed by the user.
 * targetBracket vs usesPercentage determines which extraction function is used:
 *   targetBracket=true  → extractBracketNumber ("3 (75)" → 75)
 *   usesPercentage=true → extractPercentage     ("3 (75.0%)" → 75.0)
 *   neither             → extractRawNumber       ("3 (75.0%)" → 3)
 */
interface ParsedFilterExpression {
  operator: '>' | '>=' | '<' | '<=' | '='
  value: number
  usesPercentage: boolean
  /** When true, compare against the number inside parentheses (bracket value) */
  targetBracket: boolean
}

export function isPercentageFormat(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) {
    return false
  }

  return PERCENTAGE_PATTERN.test(value)
}

export function extractPercentage(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  const match = value.match(PERCENTAGE_PATTERN)
  return match ? parseFloat(match[1]) : null
}

export function extractRawNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  const match = value.trim().match(/^-?\d+(?:\.\d+)?/)
  if (!match) {
    return null
  }

  const parsed = parseFloat(match[0])
  return Number.isFinite(parsed) ? parsed : null
}

/** Extract the number inside parentheses, with or without %. E.g. "3 (75.0%)" → 75.0, "3 (75.0)" → 75.0 */
export function extractBracketNumber(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  const match = value.match(BRACKET_NUMBER_PATTERN)
  return match ? parseFloat(match[1]) : null
}

/** Sort comparator for clinical count (pct) cells — compares bracket percentage when present. */
export function comparePercentageCellValues(valueA: unknown, valueB: unknown): number {
  const pctA = extractPercentage(valueA)
  const pctB = extractPercentage(valueB)

  if (pctA !== null && pctB !== null) {
    return pctA - pctB
  }

  const numA = extractRawNumber(valueA)
  const numB = extractRawNumber(valueB)
  if (numA !== null && numB !== null) {
    return numA - numB
  }

  return String(valueA ?? '').localeCompare(String(valueB ?? ''))
}

function parseFilterExpression(filterText: string): ParsedFilterExpression | null {
  // Try bracket-prefixed pattern first: "(>10", "(<50"
  const bracketMatch = filterText.match(BRACKET_FILTER_PATTERN)
  if (bracketMatch) {
    const value = parseFloat(bracketMatch[2])
    if (!Number.isFinite(value)) return null
    return {
      operator: bracketMatch[1] as ParsedFilterExpression['operator'],
      value,
      usesPercentage: false,
      targetBracket: true,
    }
  }

  // Standard pattern: ">5", "<=3", ">50%"
  const match = filterText.match(FILTER_EXPRESSION_PATTERN)
  if (!match) {
    return null
  }

  const value = parseFloat(match[2])
  if (!Number.isFinite(value)) {
    return null
  }

  return {
    operator: match[1] as ParsedFilterExpression['operator'],
    value,
    usesPercentage: Boolean(match[3]),
    targetBracket: false,
  }
}

/**
 * Entry point for expression-based filtering. Intercepts ag-grid's "contains" filter
 * option: if the user typed an expression (e.g. ">5"), delegates to numeric comparison;
 * otherwise falls back to case-insensitive substring match.
 */
export function matchesSmartFilter(cellValue: unknown, filterText: string): boolean {
  const normalizedFilterText = filterText.trim()
  if (!normalizedFilterText) {
    return true
  }

  const expression = parseFilterExpression(normalizedFilterText)
  if (expression) {
    const numericValue = expression.targetBracket
      ? extractBracketNumber(cellValue)
      : expression.usesPercentage
        ? extractPercentage(cellValue)
        : extractRawNumber(cellValue)
    if (numericValue === null) {
      return false
    }

    switch (expression.operator) {
      case '>':
        return numericValue > expression.value
      case '>=':
        return numericValue >= expression.value
      case '<':
        return numericValue < expression.value
      case '<=':
        return numericValue <= expression.value
      case '=':
        return numericValue === expression.value
      default:
        return false
    }
  }

  const normalizedCell =
    cellValue !== undefined && cellValue !== null ? String(cellValue).toLowerCase() : ''
  return normalizedCell.includes(normalizedFilterText.toLowerCase())
}

/**
 * Detect if a row looks like a spanning title/footer — all cells identical
 * or only the first cell non-empty (how Word represents merged cells).
 */
function isSpanningRow(row: string[]): boolean {
  if (row.length === 0) return false
  const trimmed = row.map((c) => c.trim())
  const firstNonEmpty = trimmed.find((c) => c.length > 0)
  if (!firstNonEmpty) return false

  const nonEmptyCells = trimmed.filter((c) => c.length > 0)
  if (nonEmptyCells.length === 1) return true

  return nonEmptyCells.every((c) => c === firstNonEmpty)
}

export interface TableTitleFooter {
  title: string
  footer: string
  /** The remaining rows (without title/footer) to feed into buildColumnDefsAndRowData */
  bodyValues: string[][]
}

/**
 * Extract a spanning title row (first) and/or footer row (last) from raw
 * table values. Returns the cleaned body values for grid consumption.
 */
export function extractTitleAndFooter(values: string[][]): TableTitleFooter {
  if (!values || values.length < 2) {
    return { title: '', footer: '', bodyValues: values ?? [] }
  }

  let title = ''
  let footer = ''
  let startIndex = 0
  let endIndex = values.length

  // Title: first row if it's a spanning row and there are at least 3 rows (title + header + data)
  if (values.length >= 3 && isSpanningRow(values[0])) {
    const firstNonEmpty = values[0].map((c) => c.trim()).find((c) => c.length > 0) ?? ''
    title = firstNonEmpty
    startIndex = 1
  }

  // Footer: last row if it's a spanning row and there are enough rows left
  const remainingRows = endIndex - startIndex
  if (remainingRows >= 3 && isSpanningRow(values[endIndex - 1])) {
    const firstNonEmpty = values[endIndex - 1].map((c) => c.trim()).find((c) => c.length > 0) ?? ''
    footer = firstNonEmpty
    endIndex -= 1
  }

  return { title, footer, bodyValues: values.slice(startIndex, endIndex) }
}

export function buildColumnDefsAndRowData(values: string[][]): TableGridData {
  if (!values || values.length < 2) {
    return {
      columnDefs: [],
      rowData: [],
    }
  }

  const headers = values[0]
  const dataRows = values.slice(1)

  const columnDefs: ColDef[] = headers.map((header, colIndex) => {
    const field = `col${colIndex}`
    const sampleValues = dataRows.map((row) => row[colIndex])
    const hasPercentageFormat = sampleValues.some((cell) => isPercentageFormat(cell))

    if (hasPercentageFormat) {
      return {
        headerName: header || `Col ${colIndex + 1}`,
        field,
        sortable: true,
        comparator: comparePercentageCellValues,
        // filter/filterParams inherited from defaultColDef (all options + smart textMatcher)
        valueFormatter: (params) =>
          params.data?.[field] !== undefined && params.data?.[field] !== null
            ? String(params.data[field])
            : '',
      }
    }

    return {
      headerName: header || `Col ${colIndex + 1}`,
      field,
      sortable: true,
      // filter/filterParams inherited from defaultColDef (all options + smart textMatcher)
      filterValueGetter: (params) => {
        const raw = params.data?.[field]
        return raw !== undefined && raw !== null ? String(raw).trim() : ''
      },
    }
  })

  const rowData = dataRows.map((row) => {
    const mappedRow: Record<string, string> = {}
    headers.forEach((_, index) => {
      mappedRow[`col${index}`] =
        row[index] !== undefined && row[index] !== null ? String(row[index]) : ''
    })
    return mappedRow
  })

  return { columnDefs, rowData }
}
